
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useInvoiceFormData } from './hooks/useInvoiceFormData';
import { useInvoiceCalculations } from './hooks/useInvoiceCalculations';
import { useInvoiceCreation } from './hooks/useInvoiceCreation';
import { useChangeOrderWarning } from '@/hooks/useChangeOrderWarning';
import InvoiceProgressSummary from './components/InvoiceProgressSummary';
import InvoiceFormWrapper from './components/InvoiceFormWrapper';
import InvoiceFormContent from './components/InvoiceFormContent';
import ChangeOrderWarningDialog from './components/ChangeOrderWarningDialog';
import CreditSuggestionBanner from './components/CreditSuggestionBanner';
import ApplyCreditsToInvoiceModal from '@/components/customers/credits/ApplyCreditsToInvoiceModal';
import { useCustomerCredits, CustomerCredit } from '@/hooks/useCustomerCredits';

const invoiceFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  due_date: z.date().optional(),
  payment_terms: z.string().optional(),
  special_instructions: z.string().optional(),
  invoice_type: z.enum(['full', 'partial', 'custom_amount', 'percentage']),
  percentage_of_estimate: z.number().min(1).max(100).optional(),
  custom_amount: z.number().min(0).optional(),
  selected_line_items: z.array(z.string()).optional(),
});

type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

interface EnhancedInvoiceFormProps {
  estimateId: string | null;
}

interface LineItemPercentage {
  [key: string]: number;
}

const EnhancedInvoiceForm: React.FC<EnhancedInvoiceFormProps> = ({ estimateId }) => {
  const [selectedLineItems, setSelectedLineItems] = useState<string[]>([]);
  const [lineItemPercentages, setLineItemPercentages] = useState<LineItemPercentage>({});
  const [showChangeOrderWarning, setShowChangeOrderWarning] = useState(false);
  const [showCreditBanner, setShowCreditBanner] = useState(true);
  const [showApplyCreditModal, setShowApplyCreditModal] = useState(false);
  const [selectedCreditForApply, setSelectedCreditForApply] = useState<CustomerCredit | null>(null);
  
  const { estimate, lineItems, existingInvoices, isLoadingEstimate, totalAvailableCredit, hasAvailableCredit, customerCredits } = useInvoiceFormData(estimateId);
  const { estimateTotal, totalInvoiced, remainingBalance } = useInvoiceCalculations(lineItems || [], existingInvoices || [], estimate?.adjustment_metadata as any);
  const createInvoiceMutation = useInvoiceCreation(estimateId);
  const { applyCredit, isApplying } = useCustomerCredits(estimate?.customer_id || null);
  const { 
    pendingChangeOrders, 
    checkForPendingChangeOrders, 
    approveChangeOrders, 
    isLoading: isCheckingChangeOrders 
  } = useChangeOrderWarning();

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      title: '',
      description: '',
      payment_terms: '',
      special_instructions: '',
      invoice_type: 'full',
      selected_line_items: [],
    },
  });

  const invoiceType = form.watch('invoice_type');
  const percentageOfEstimate = form.watch('percentage_of_estimate');
  const customAmount = form.watch('custom_amount');

  // Calculate invoice amount in real-time
  const calculateInvoiceAmount = () => {
    switch (invoiceType) {
      case 'full':
        return remainingBalance;
      case 'percentage':
        return percentageOfEstimate ? (estimateTotal * percentageOfEstimate) / 100 : 0;
      case 'custom_amount':
        return customAmount || 0;
      case 'partial':
        if (!lineItems || selectedLineItems.length === 0) return 0;
        const selectedItems = lineItems.filter(item => selectedLineItems.includes(item.id));
        return selectedItems.reduce((sum, item) => {
          const percentage = lineItemPercentages[item.id] || 100;
          const itemCost = item.manual_override && item.manual_total_cost !== null
            ? item.manual_total_cost
            : item.total_cost_snapshot;
          return sum + (itemCost * percentage / 100);
        }, 0);
      default:
        return 0;
    }
  };

  const currentInvoiceAmount = calculateInvoiceAmount();
  const remainingAfterInvoice = remainingBalance - currentInvoiceAmount;

  useEffect(() => {
    if (estimate) {
      form.reset({
        title: `Invoice for ${estimate.title}`,
        description: estimate.description || '',
        payment_terms: estimate.payment_terms || '',
        special_instructions: estimate.special_instructions || '',
        invoice_type: 'full',
        selected_line_items: [],
      });
    }
  }, [estimate, form]);

  const onSubmit = async (values: InvoiceFormValues) => {
    if (!estimate || !lineItems) {
      console.error('Missing estimate or line items');
      return;
    }

    // Check for pending change orders before creating invoice
    const changeOrders = await checkForPendingChangeOrders(estimateId!);
    if (changeOrders.length > 0) {
      setShowChangeOrderWarning(true);
      return;
    }

    // Proceed with invoice creation
    proceedWithInvoiceCreation(values);
  };

  const proceedWithInvoiceCreation = (values: InvoiceFormValues) => {
    createInvoiceMutation.mutate({
      values: { 
        ...values, 
        totalInvoiced,
        selected_line_items: selectedLineItems 
      },
      estimate,
      lineItems,
      selectedLineItems,
      estimateTotal,
      lineItemPercentages,
      existingInvoices
    });
  };

  const handleApproveChangeOrders = async () => {
    const changeOrderIds = pendingChangeOrders.map(co => co.id);
    const success = await approveChangeOrders(changeOrderIds);
    if (success) {
      setShowChangeOrderWarning(false);
      // After approving, proceed with invoice creation using the form values
      const formValues = form.getValues();
      proceedWithInvoiceCreation(formValues);
    }
  };

  const handleContinueAnyway = () => {
    setShowChangeOrderWarning(false);
    const formValues = form.getValues();
    proceedWithInvoiceCreation(formValues);
  };

  const handleLineItemSelection = (itemId: string, checked: boolean) => {
    if (checked) {
      const newSelectedItems = [...selectedLineItems, itemId];
      setSelectedLineItems(newSelectedItems);
      setLineItemPercentages(prev => ({ ...prev, [itemId]: 100 }));
      // Update form field
      form.setValue('selected_line_items', newSelectedItems);
    } else {
      const newSelectedItems = selectedLineItems.filter(id => id !== itemId);
      setSelectedLineItems(newSelectedItems);
      setLineItemPercentages(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      // Update form field
      form.setValue('selected_line_items', newSelectedItems);
    }
  };

  const handleLineItemPercentageChange = (itemId: string, percentage: number) => {
    setLineItemPercentages(prev => ({ ...prev, [itemId]: percentage }));
  };

  const handleApplyCreditFromBanner = () => {
    // Use first available credit for simplicity
    if (customerCredits && customerCredits.length > 0) {
      setSelectedCreditForApply(customerCredits[0] as CustomerCredit);
      setShowApplyCreditModal(true);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Credit Suggestion Banner */}
      {hasAvailableCredit && showCreditBanner && (
        <CreditSuggestionBanner
          totalAvailableCredit={totalAvailableCredit}
          onApply={handleApplyCreditFromBanner}
          onDismiss={() => setShowCreditBanner(false)}
        />
      )}

      <InvoiceProgressSummary
        estimateTotal={estimateTotal}
        totalInvoiced={totalInvoiced}
        remainingBalance={remainingBalance}
      />

      <InvoiceFormWrapper
        estimateId={estimateId}
        isLoadingEstimate={isLoadingEstimate}
        estimate={estimate}
      >
        <InvoiceFormContent
          form={form}
          invoiceType={invoiceType}
          remainingBalance={remainingBalance}
          lineItems={lineItems}
          selectedLineItems={selectedLineItems}
          lineItemPercentages={lineItemPercentages}
          currentInvoiceAmount={currentInvoiceAmount}
          remainingAfterInvoice={remainingAfterInvoice}
          estimateId={estimateId!}
          isCreating={createInvoiceMutation.isPending || isCheckingChangeOrders}
          onSubmit={onSubmit}
          onLineItemSelection={handleLineItemSelection}
          onLineItemPercentageChange={handleLineItemPercentageChange}
        />
      </InvoiceFormWrapper>

      <ChangeOrderWarningDialog
        isOpen={showChangeOrderWarning}
        onClose={() => setShowChangeOrderWarning(false)}
        onContinue={handleContinueAnyway}
        onApproveChangeOrder={handleApproveChangeOrders}
        changeOrderCount={pendingChangeOrders.length}
      />

      {/* Apply Credit Modal */}
      {estimate?.customer_id && (
        <ApplyCreditsToInvoiceModal
          isOpen={showApplyCreditModal}
          onClose={() => {
            setShowApplyCreditModal(false);
            setSelectedCreditForApply(null);
          }}
          credit={selectedCreditForApply}
          customerId={estimate.customer_id}
          onApply={applyCredit}
          isLoading={isApplying}
        />
      )}
    </div>
  );
};

export default EnhancedInvoiceForm;
